import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { BookingFormProps } from './types';
import { bookMeeting } from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faGlobe, faCalendarAlt, faUser, faEnvelope, faCheck, faPaperclip, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './BookingForm.css';

const BookingForm: React.FC<BookingFormProps> = ({
    eventId,
    eventTitle,
    selectedTime,
    displayTime,
    duration,
    timezone,
    clientId,
    onSuccess,
    onBack,
    initialError
}) => {
    // Helper function to format ISO time string for display in the user's timezone
    function formatTimeForDisplay(isoTime: string, timezone: string): string {
      try {
        if (!isoTime) return "Unknown time";
        
        // Ensure the ISO string has a 'Z' suffix for UTC if no timezone is specified
        const isoTimeWithZ = isoTime.endsWith('Z') ? isoTime : `${isoTime}Z`;
        
        // Parse the ISO string
        const date = parseISO(isoTimeWithZ);
        
        // Format date and time using the specified timezone
        return formatInTimeZone(date, timezone, "EEEE, MMMM d, yyyy 'at' h:mm a");
      } catch (error) {
        console.error("Error formatting time for display:", error);
        return isoTime || "Unknown time";
      }
    }

    // Helper function to format duration between two ISO time strings
    function formatDuration(startIso: string, endIso: string, timezone: string): string {
      try {
        if (!startIso || !endIso) return "";
        
        // Ensure both ISO strings have 'Z' suffixes for UTC
        const startWithZ = startIso.endsWith('Z') ? startIso : `${startIso}Z`;
        const endWithZ = endIso.endsWith('Z') ? endIso : `${endIso}Z`;
        
        // Parse the ISO strings
        const startDate = parseISO(startWithZ);
        const endDate = parseISO(endWithZ);
        
        // Calculate duration in minutes
        const durationMs = endDate.getTime() - startDate.getTime();
        const durationMinutes = Math.round(durationMs / (1000 * 60));
        
        // Format the duration
        if (durationMinutes < 60) {
          return `${durationMinutes} minutes`;
        } else {
          const hours = Math.floor(durationMinutes / 60);
          const minutes = durationMinutes % 60;
          if (minutes === 0) {
            return `${hours} hour${hours > 1 ? 's' : ''}`;
          } else {
            return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
          }
        }
      } catch (error) {
        console.error("Error formatting duration:", error);
        return "";
      }
    }

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(initialError || null);
    const [bookingResponse, setBookingResponse] = useState<any>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    
    // For debugging
    const [debugInfo, setDebugInfo] = useState<any>({
        eventId,
        selectedTime,
        timezone,
        displayTime
    });
    
    // Update error state when initialError prop changes
    useEffect(() => {
        if (initialError) {
            setError(initialError);
        }
    }, [initialError]);
    
    // Get user's local timezone for display purposes
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Function to format the selected time for display
    const formattedTime = useMemo(() => {
        console.log('BookingForm rendering with:', { selectedTime, displayTime });
        
        // If displayTime is provided, use it directly without any conversion
        if (displayTime) {
            // Format: Extract date from ISO string if available, but don't convert timezone
            let dateString = '';
            if (selectedTime && selectedTime.includes('T')) {
                try {
                    // Get date only from the ISO string (ignore time part)
                    const isoDate = selectedTime.split('T')[0];
                    const [year, month, day] = isoDate.split('-').map(Number);
                    
                    // Create a date object from the date parts
                    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                        // Note: months are 0-indexed in JavaScript Date
                        const date = new Date(year, month - 1, day);
                        dateString = format(date, 'EEEE, MMMM d, yyyy');
                    }
                } catch (e) {
                    console.error('Error extracting date from ISO string:', e);
                }
            }
            
            // If we have a date string, just append the display time, otherwise just show the display time
            return dateString ? `${dateString} ${displayTime}` : displayTime;
        }

        // Fallback if no displayTime is provided (should rarely happen)
        try {
            if (selectedTime && selectedTime.includes('T')) {
                // ISO string without timezone conversion - just extract and format
                const isoDate = selectedTime.split('T')[0];
                const isoTime = selectedTime.split('T')[1].replace('Z', '');
                
                // Get date parts
                const [year, month, day] = isoDate.split('-').map(Number);
                // Get time parts
                const [hours, minutes] = isoTime.split(':').map(Number);
                
                if (!isNaN(year) && !isNaN(month) && !isNaN(day) && !isNaN(hours) && !isNaN(minutes)) {
                    // Create the date object without timezone conversion
                    const date = new Date(year, month - 1, day);
                    const dateStr = format(date, 'EEEE, MMMM d, yyyy');
                    
                    // Format time in 12-hour format without timezone conversion
                    const period = hours >= 12 ? 'PM' : 'AM';
                    const hours12 = hours % 12 || 12;
                    const timeStr = `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
                    
                    return `${dateStr} ${timeStr}`;
                }
            }
            
            // Last fallback - return whatever we have
            return selectedTime || 'Unknown time';
        } catch (e) {
            console.error('Error formatting time:', e);
            return selectedTime || 'Unknown time';
        }
    }, [selectedTime, displayTime]);
    
    // Show timezone information - use the Cal.com timezone
    const timezoneDisplay = timezone || 'UTC';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate form
        if (!name.trim()) {
            setError('Please enter your name');
            return;
        }
        
        if (!email.trim()) {
            setError('Please enter your email');
            return;
        }
        
        // Simple email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }
        
        // Check if clientId is available
        if (!clientId) {
            setError('Client ID is missing. Please refresh the page and try again.');
            return;
        }
        
        // Ensure the selectedTime is valid
        if (!selectedTime) {
            setError('No time selected. Please go back and select a time slot.');
            return;
        }
        
        // CRITICAL FIX: Use the EXACT time received from Cal.com without any changes
        // Just verify it ends with Z as required by Cal.com API
        let bookingTime = selectedTime;
        if (!bookingTime.endsWith('Z')) {
            bookingTime = `${bookingTime}Z`;
            console.log(`[BookingForm] Added Z suffix for Cal.com API compatibility: ${bookingTime}`);
        }
        
        // Log the exact time we're going to send to Cal.com
        console.log('[BookingForm] EXACTLY sending this time to Cal.com:', bookingTime);
        console.log('[BookingForm] Display time shown to user:', displayTime);
        
        setIsSubmitting(true);
        setError(null);
        
        try {
            // CRITICAL: Use the EXACT time string with no modifications
            const response = await bookMeeting({
                event_type_id: eventId,
                start_time: bookingTime, // Use the EXACT time string
                user_name: name,
                user_email: email,
                notes,
                timezone: "UTC",  // Always use UTC for consistency
                client_id: clientId,
                duration: duration
            });
            
            console.log('[BookingForm] Booking response:', response);
            
            // Call onSuccess with the response if it exists
            if (onSuccess) {
                onSuccess(response);
            }
            
            // Reset form
            setName('');
            setEmail('');
            setNotes('');
            
        } catch (error: any) {
            console.error('[BookingForm] Error booking meeting:', error);
            
            // Extract error message with special handling for Cal.com specific errors
            let errorMessage = 'Failed to book meeting';
            
            if (error.message) {
                errorMessage = error.message;
                
                // Add user-friendly suggestions for common errors
                if (errorMessage.includes('already has booking') || errorMessage.includes('not available')) {
                    errorMessage = 'This time slot is no longer available. Please select a different time.';
                } else if (errorMessage.includes('outside of availability')) {
                    errorMessage = 'This time is outside of the host\'s working hours. Please select a time during business hours.';
                }
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            }
            
            setError(errorMessage);
            
            // Still call onSuccess with the error if it exists
            if (onSuccess) {
                onSuccess({
                    success: false,
                    error: errorMessage
                });
            }
            
        } finally {
            setIsSubmitting(false);
        }
    };

    // Add an auto-hide timer for the confirmation
    useEffect(() => {
        let hideTimer: NodeJS.Timeout;
        
        if (showConfirmation && bookingResponse) {
            // Set a timer to auto-hide the confirmation after 30 seconds
            hideTimer = setTimeout(() => {
                console.log('Auto-hiding booking confirmation after timeout');
                onSuccess?.({ success: true, autoHide: true });
            }, 30000); // 30 seconds
        }
        
        return () => {
            if (hideTimer) clearTimeout(hideTimer);
        };
    }, [showConfirmation, bookingResponse]);

    // Update debug info when props change
    useEffect(() => {
        setDebugInfo({
            eventId,
            selectedTime,
            timezone,
            displayTime
        });
    }, [eventId, selectedTime, timezone, displayTime]);

    if (showConfirmation && bookingResponse) {
        return (
            <div className="booking-form-confirmation">
                <div className="booking-confirmation-header">
                    <FontAwesomeIcon icon={faCheck} className="confirmation-icon" />
                    <h2>Booking Confirmed!</h2>
                </div>
                
                <div className="booking-details">
                    <h3>{eventTitle}</h3>
                    
                    <div className="booking-detail-item">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>
                            {formatTimeForDisplay(bookingResponse.start_time || selectedTime, userTimezone)}
                            {bookingResponse.start_time && bookingResponse.end_time && (
                                <span className="booking-duration">
                                    ({formatDuration(bookingResponse.start_time, bookingResponse.end_time, userTimezone)})
                                </span>
                            )}
                        </span>
                    </div>
                    
                    <div className="booking-detail-item">
                        <FontAwesomeIcon icon={faGlobe} />
                        <span>All times shown in your local timezone: {userTimezone}</span>
                    </div>
                    
                    <div className="booking-detail-item">
                        <FontAwesomeIcon icon={faUser} />
                        <span>{name}</span>
                    </div>
                    
                    <div className="booking-detail-item">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <span>{email}</span>
                    </div>
                    
                    {notes && (
                        <div className="booking-detail-item">
                            <FontAwesomeIcon icon={faPaperclip} />
                            <span>{notes}</span>
                        </div>
                    )}
                </div>
                
                <div className="booking-confirmation-footer">
                    <p>A confirmation email has been sent to {email}</p>
                    <button className="booking-close-button" onClick={() => onSuccess?.(bookingResponse)}>
                        Close
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="booking-form">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Event Information */}
                <div className="event-info-card">
                    <h3 className="event-info-title">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                        Book Your {eventTitle}
                    </h3>
                    <div className="event-info-details">
                        <div className="event-info-item">
                            <FontAwesomeIcon icon={faClock} className="event-info-icon" />
                            <span className="event-info-date">{formattedTime}</span>
                        </div>
                        <div className="event-info-item">
                            <FontAwesomeIcon icon={faGlobe} className="event-info-icon" />
                            <span>{timezoneDisplay}</span>
                        </div>
                        <div className="event-info-item">
                            <span className="event-duration">{duration} minutes</span>
                        </div>
                    </div>
                </div>
                
                {/* Error Message */}
                {error && (
                    <div className="error-message-container">
                        <div className="error-message-content">
                            <p>{error}</p>
                        </div>
                    </div>
                )}
                
                {/* Form Fields */}
                <div className="space-y-4">
                    <div>
                        <label htmlFor="name" className="form-label">
                            <FontAwesomeIcon icon={faUser} className="form-label-icon" />
                            Your Name
                        </label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="form-input"
                            placeholder="John Doe"
                            required
                        />
                    </div>
                    
                    <div>
                        <label htmlFor="email" className="form-label">
                            <FontAwesomeIcon icon={faEnvelope} className="form-label-icon" />
                            Your Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="form-input"
                            placeholder="john@example.com"
                            required
                        />
                    </div>
                    
                    <div>
                        <label htmlFor="notes" className="form-label">
                            <FontAwesomeIcon icon={faPaperclip} className="form-label-icon" />
                            Additional Notes (Optional)
                        </label>
                        <textarea
                            id="notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="form-textarea"
                            placeholder="Any additional information you'd like to share..."
                        />
                    </div>
                </div>
                
                {/* Form Actions */}
                <div className="form-footer">
                    <button
                        type="button"
                        onClick={onBack}
                        className="back-button"
                    >
                        Back
                    </button>
                    
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} className="spinner-icon" />
                                Booking...
                            </>
                        ) : (
                            'Confirm Booking'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BookingForm; 